import React, { memo, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ReactModal from 'react-modal';
import moment from 'moment';

import { ActionButton } from '../../../common';
import { listRoles, viewDocument, viewReports } from '../../../../helpers/data-constants';
import { extractValue } from '../../../../helpers/common';
import { messages } from '../../../../helpers/messages';
import { HookForm, HInput, HDatePicker, DSelect, HTextarea } from '../../../../validation/HForm';
import { thingsSchema, puritiesShema, anotherSchema, internalMovementSchema } from './toFormRegisterModalShema';
import RegisterModalDetails from './RegisterModalDetails';
import BasicAlert from '../../Alert/Alert';
import { clearError, getDepartments } from '../../../../redux/actions/handbookAction';
import { getPrintedForm } from '../../../../redux/actions/contractAction';
import { Message } from '../..';

import './toFormRegisterModal.scss';

import PrintIcon from '../../../../assets/icons/print.svg';

const defaultFormState = {
  documentNumber: '',
  reason: '',
  transferDate: moment().format('YYYY-MM-DD'),
};

const ToFormRegisterModal = memo(
  ({
    printedFormButton,
    isOpen,
    handleCloseModal,
    view,
    cashBoxInfo,
    dealIds,
    submitTransferDeals,
    permissibleDate,
  }) => {
    let form = {};
    const { loading, commonError, departments } = useSelector(({ handbook }) => handbook);
    const { accessFeatures, userInfo } = useSelector(({ user }) => user);
    const dispatch = useDispatch();
    const [error, setError] = useState('');
    const [selectedSendApplierId, setSelectedSendApplierId] = useState(undefined)
    const isViewThings = view === viewDocument.isTN ? viewDocument.isTN : false;
    const isViewPurities = view === viewDocument.isRegister ? viewDocument.isRegister : false;
    const isTransferAnotherStore =
    !isViewPurities && !isViewThings ? viewDocument.isTransferAnotherStore : false;
    
    const isInternalMovement = accessFeatures['internal.movement.delivery.invoice']
    const isMainEnable = accessFeatures['cash_box.main.enable']
    const isDirectionMessage = commonError === messages[16007]     
    const isAccessRole = userInfo?.roles.includes(listRoles.GOODS_SPECIALIST) || userInfo?.roles.includes(listRoles.ADMIN);
    const shouldRenderRegisterModal = (isInternalMovement && isViewPurities) || (!isInternalMovement && isViewThings);
    const shouldShowExampleReason = !isTransferAnotherStore && isInternalMovement && isViewThings;
    const defaultReason = isViewThings && !isInternalMovement ? 'Распоряжение №1 от 28.01.2017' : undefined

    const defaultOptions = useMemo(() => {
      if (!isDirectionMessage && isViewPurities && departments.length) {
        return {
          label: departments[0].label,
          value: departments[0].value,
        };
      }
      return { label: '', value: '' };
    }, [isMainEnable, isViewPurities, departments]);

    useEffect(() => {
      if(isMainEnable) dispatch(getDepartments("", false, true));
      return () => {
        dispatch(clearError());
    };
    }, [isMainEnable, dispatch])

    const handleOpenClose = () => {
      if (isViewPurities) return handleCloseModal(isViewPurities);
      if (isViewThings) return handleCloseModal(isViewThings);
      if (isTransferAnotherStore) return handleCloseModal(isTransferAnotherStore);
    };

    const handleSubmit = (data) => {
      const reqData = {
        documentNumber: data.documentNumber,
        transferDate: moment(data.transferDate).format('YYYY-MM-DD'),
        reason: data.reason,
        destCashBoxId: data.destCashBoxId.value,
      };

      const internalMovementData = {
        sendCashBoxId: cashBoxInfo.id,
        recipientCashBoxId: data.destCashBoxId.value,
        sendApplierId: extractValue(data.sendApplierId),
        senderId: extractValue(data.senderId),
        deliverReceptionistId: extractValue(data.deliverReceptionistId),
        accepterId: extractValue(data.accepterId),
        date: moment(data.transferDate).format('YYYY-MM-DD')
      };

      if (isViewPurities) {
        reqData.type = 'gohran';
        reqData.dealIds = dealIds;
      }

      if (isViewThings) {
        reqData.type = 'store';
        reqData.dealIds = dealIds;
        reqData.accepterId = !isInternalMovement ? extractValue(data.accepterId) : undefined
      }

      if (isTransferAnotherStore) {
        reqData.type = 'anotherstore';
      }

      const payload = isInternalMovement && isViewPurities ? {reqData, internalMovementData} : reqData
      submitTransferDeals(payload);
      if (isViewPurities) return handleCloseModal(isViewPurities);
      if (isViewThings) return handleCloseModal(isViewThings);
      if (isTransferAnotherStore) return handleCloseModal(isTransferAnotherStore);
    };

    const handleOptionsCashbox = (e, action) => {
      if (action.action === 'input-change') dispatch(getDepartments(e));
      setSelectedSendApplierId(form.watch('destCashBoxId'))
    };

    const addReason = () => {
      form.setValue('reason', accessFeatures['example_transfer_order'] ? 'ПРИКАЗ №238 ОТ 31.05.2016' : 'ПРИКАЗ №201 ОТ 07.10.2024');
    };

    const returnShema = () => {
      if (isViewPurities) return isInternalMovement ? internalMovementSchema({isAccessRole}) : puritiesShema;
      if (isViewThings) return !isInternalMovement ? internalMovementSchema({isViewThings}) : thingsSchema;
      if (isTransferAnotherStore) return anotherSchema;
    };

    const callPrintedForm = () => {
      const data = form.getValues();

      const filename =
        'exp_' +
        data.documentNumber +
        '_' +
        moment(data.transferDate).format('DD.MM.YYYY') +
        '.pdf';
      let viewReport;

      if (isViewPurities) viewReport = viewReports.INTERNAL_MOVEMENT_GOHRAN;
      if (isViewThings) viewReport = viewReports.INTERNAL_MOVEMENT_STORE;

      dispatch(
        getPrintedForm(
          {
            dealIds: dealIds.join(','),
          },
          filename,
          viewReport,
        ),
      );
    };

    return (
      <ReactModal
        isOpen={isOpen}
        contentLabel="Modal"
        className="modal-container toFormRegisterModal-modal-container overflow-modal"
        overlayClassName="modal-overlay">
        <HookForm
          onSubmit={handleSubmit}
          validationSchema={returnShema()}
          defaultValues={defaultFormState}
          predef={(methods) => (form = methods)}>
          {commonError && !isDirectionMessage && <Message type="error" text={commonError} />}
          <div className="modal-header">
            {(isViewThings || isTransferAnotherStore) && <h2>Сформировать товарную накладную</h2>}
            {isViewPurities && <h2>Сформировать опись</h2>}
            { 
              printedFormButton &&
              <button type="button" className="button-icontrigger" onClick={callPrintedForm}>
                <span className="icon">
                  <PrintIcon />
                </span>
              </button>
            }
          </div>
          <div className="modal-body">
            <div>
              <p>
                Подразделение: <b>{cashBoxInfo.name}</b>
              </p>
              <p>Адрес: {cashBoxInfo.address}</p>
            </div>
            <div className="form-element">
              <label className="form-element-label" htmlFor="documentNumber">
                Номер документа
              </label>
              <div className="form-element-data combobox-lg">
                <HInput h="true" name="documentNumber" className="input" type="text" decimals={0} />
              </div>
            </div>
            <div className="toFormRegisterModal-container">
              {!isTransferAnotherStore && (
                <div className="form-element">
                  <label className="form-element-label" htmlFor="transferDate">
                    Дата передачи
                  </label>
                  <div className="form-element-data date toFormRegisterModal-container-item">
                    <HDatePicker h="true" className="input" name="transferDate" id="transferDate" />

                    <HDatePicker
                      h="true"
                      type="hidden"
                      name="permissibleDate"
                      id="permissibleDate"
                      defaultValue={moment().subtract(permissibleDate, 'days').format('YYYY-MM-DD')}
                    />
                  </div>
                </div>
              )}
              <div className="form-element">
                <label className="form-element-label" htmlFor="destCashBoxId">
                  Получатель
                </label>
                <div className="form-element-data toFormRegisterModal-container-input">
                  <DSelect
                    h="true" 
                    name="destCashBoxId"
                    placeholder="Введите название"
                    defaultValue={defaultOptions}
                    options={departments}
                    isLoading={loading}
                    handleOptions={handleOptionsCashbox}
                  />
                </div>
              </div>            
            </div>
            {shouldRenderRegisterModal && <RegisterModalDetails h="true" view={view} selectedSendApplierId={selectedSendApplierId}/>}
            {(isViewThings || isTransferAnotherStore) && (
              <div className="form-element">
                <label className="form-element-label" htmlFor="reason">
                  Основание
                </label>
                <div className="form-element-data textarea">
                  <HTextarea h="true" className="input" name="reason" placeholder={defaultReason}/>
                  {shouldShowExampleReason && (
                    <p className="toFormRegisterModal-reason">
                      Например: <span onClick={addReason}>{accessFeatures['example_transfer_order'] ? 'ПРИКАЗ №238 ОТ 31.05.2016' : 'ПРИКАЗ №201 ОТ 07.10.2024'}</span>
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer toFormRegisterModal-modal-footer">
            <ActionButton text="Сохранить" styleName="button" submit />
            <ActionButton text="Отменить" func={handleOpenClose} styleName="button-light" />
          </div>
        </HookForm>
        {isViewPurities && isMainEnable && <BasicAlert onClose={() => setError('')}
          message={isDirectionMessage ? commonError : ''}
          isOpen={isDirectionMessage}
          isError={true}
        />}
      </ReactModal>
    );
  },
);

export default ToFormRegisterModal;
